import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { palette } from '../palette';
import { Email, Google } from '@mui/icons-material';
import { colors } from '@mui/material';
import React, { PropsWithChildren } from 'react';
import { useLoginButtonController } from './useLoginButtonController';

interface LoginButtonProps extends PropsWithChildren {
  onLoginClicked?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  authenticationRedirectUrl?: string;
  loginButtonHelp: string | React.ReactElement;
}

export function LoginButton({ children, loginButtonHelp, onLoginClicked, disabled = false }: LoginButtonProps) {
  const {
    authUrl,
    email,
    emailError,
    magicLinkSent,
    magicLinkLoading,
    magicLinkError,
    handleEmailChange,
    handleSendMagicLink,
    handleKeyDown,
    handleTryDifferentEmail,
  } = useLoginButtonController();

  return (
    <Box height={'100vh'} display={'flex'} flexDirection={'column'} flex={'auto'} justifyContent={'space-around'}>
      <Grid display={'flex'} flexDirection={'row'} justifyContent={'center'}>
        <img alt="We Hate Exercise" width={'200px'} src={'./logo.png'} />
      </Grid>

      <Grid display={'flex'} flexDirection={'column'} alignItems={'center'} px={2}>
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 400,
            p: 4,
            borderRadius: 3,
            backgroundColor: palette.background.paper,
            border: `1px solid ${palette.background.border}`,
          }}
        >
          <Typography variant="h6" sx={{ textAlign: 'center', mb: 3, fontWeight: 500, color: palette.primary.dark }}>
            Welcome back
          </Typography>

          {/* Google Sign In */}
          {disabled && (
            <Tooltip title={loginButtonHelp} arrow placement={'top'}>
              <span>
                <Button disabled fullWidth variant="contained" sx={{ py: 1.25, borderRadius: 1.5 }}>
                  <Google sx={{ mr: 1, fontSize: '1.2rem', color: colors.common.white }} />
                  <Typography variant="button" color={colors.common.white}>
                    Sign In with Google
                  </Typography>
                </Button>
              </span>
            </Tooltip>
          )}
          {!disabled && (
            <Tooltip title={loginButtonHelp} arrow placement={'top'}>
              <Button
                fullWidth
                href={authUrl}
                onClick={onLoginClicked}
                variant="contained"
                sx={{
                  py: 1.25,
                  borderRadius: 1.5,
                  bgcolor: palette.primary.main,
                  '&:hover': { bgcolor: palette.primary.dark },
                }}
              >
                <Google sx={{ mr: 1, fontSize: '1.2rem', color: colors.common.white }} />
                <Typography variant="button" color={colors.common.white}>
                  Sign In with Google
                </Typography>
              </Button>
            </Tooltip>
          )}

          {/* Divider */}
          <Divider sx={{ my: 2.5, color: palette.secondary.main, fontSize: '0.85rem' }}>or sign in with email</Divider>

          {/* Magic Link Form */}
          {magicLinkSent ? (
            <Box sx={{ textAlign: 'center' }}>
              <Alert
                severity="success"
                variant="outlined"
                sx={{ mb: 2, borderRadius: 1.5, '& .MuiAlert-message': { width: '100%', textAlign: 'center' } }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Check your email for a sign-in link.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  It expires in 15 minutes.
                </Typography>
              </Alert>
              <Button
                fullWidth
                variant="text"
                size="small"
                onClick={handleTryDifferentEmail}
                sx={{ color: palette.primary.main, textTransform: 'none' }}
              >
                Use a different email
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="you@example.com"
                label="Email address"
                type="email"
                value={email}
                onChange={handleEmailChange}
                onKeyDown={handleKeyDown}
                error={!!emailError}
                helperText={emailError}
                disabled={magicLinkLoading}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ fontSize: '1.1rem', color: palette.secondary.main }} />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
                }}
              />
              {magicLinkError && (
                <Alert severity="error" variant="outlined" sx={{ borderRadius: 1.5, py: 0 }}>
                  {magicLinkError}
                </Alert>
              )}
              <Button
                fullWidth
                variant="outlined"
                onClick={handleSendMagicLink}
                disabled={magicLinkLoading}
                sx={{
                  py: 1.25,
                  borderRadius: 1.5,
                  borderColor: palette.primary.main,
                  color: palette.primary.main,
                  fontWeight: 500,
                  '&:hover': {
                    borderColor: palette.primary.dark,
                    bgcolor: 'rgba(66, 121, 124, 0.06)',
                  },
                }}
              >
                {magicLinkLoading ? <CircularProgress size={22} /> : 'Send Magic Link'}
              </Button>
            </Box>
          )}
        </Paper>
      </Grid>
      {children}
    </Box>
  );
}
